import React, { useState, useEffect } from 'react';

const FONT = "'Inter', system-ui, sans-serif";

const Icon = ({ d, size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    send: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
    clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    check: 'M20 6L9 17l-5-5',
    alert: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    star: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'
};

export default function RequestUpdate({ user }) {
    const [message, setMessage] = useState('');
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Subscription & Stats state
    const [stats, setStats] = useState({ drivers: 0, fleet: 0, members: 0, routes: 0, passengers: 0, clients: 0 });
    const [subscription, setSubscription] = useState(null);
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : null);

    useEffect(() => {
        if (!operatorId) return;
        fetchData();
    }, [operatorId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [reqsRes, driversRes, fleetRes, membersRes, routesRes, subRes, passRes, clientsRes] = await Promise.all([
                fetch(`/api/plan-requests/operator/${operatorId}`),
                fetch(`/api/drivers/${operatorId}`),
                fetch(`/api/vehicles/${operatorId}`),
                fetch(`/api/operators/${operatorId}/members`),
                fetch(`/api/routes/${operatorId}`),
                fetch(`/api/subscriptions/operator/${operatorId}`),
                fetch(`/api/passengers/${operatorId}`),
                fetch(`/api/clients/${operatorId}`)
            ]);

            const [reqsData, drivers, fleet, members, routes, subscriptions, passengers, clients] = await Promise.all([
                reqsRes.json(), driversRes.json(), fleetRes.json(), membersRes.json(), routesRes.json(), subRes.json(), passRes.json(), clientsRes.json()
            ]);

            setRequests(Array.isArray(reqsData) ? reqsData : []);
            
            setStats({
                drivers: Array.isArray(drivers) ? drivers.length : 0,
                fleet: Array.isArray(fleet) ? fleet.length : 0,
                members: Array.isArray(members) ? members.length : 0,
                routes: Array.isArray(routes) ? routes.length : 0,
                passengers: Array.isArray(passengers) ? passengers.length : 0,
                clients: Array.isArray(clients) ? clients.length : 0,
            });

            if (Array.isArray(subscriptions)) {
                const activeSub = subscriptions.find(s => s.status === 'Active');
                if (activeSub) setSubscription(activeSub);
            }
        } catch (err) {
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim()) return;
        setSubmitting(true);
        try {
            const res = await fetch('/api/plan-requests', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ operatorId, message })
            });
            if (res.ok) {
                setMessage('');
                fetchData();
                alert('Request submitted successfully!');
            } else {
                const err = await res.json();
                alert(err.message || 'Failed to submit request');
            }
        } catch (err) {
            alert('Server error');
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Approved': return { bg: '#ecfdf5', text: '#059669', icon: icons.check, iconColor: '#059669', border: '#a7f3d0' };
            case 'Rejected': return { bg: '#fef2f2', text: '#dc2626', icon: icons.alert, iconColor: '#dc2626', border: '#fecaca' };
            default: return { bg: '#fffbeb', text: '#d97706', icon: icons.clock, iconColor: '#d97706', border: '#fde68a' };
        }
    };

    return (
        <div style={{ padding: '30px', width: '100%', boxSizing: 'border-box', fontFamily: FONT }}>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', marginBottom: '24px', letterSpacing: '-0.02em' }}>Request Plan Update</h1>
            
            {/* Active Subscription Details */}
            {subscription && subscription.planId && (
                <div style={{ marginBottom: 24, background: 'white', borderRadius: 20, border: '1px solid #e2e8f0', padding: '26px 28px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 12, background: '#f0f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon d={icons.star} size={20} color="#8b5cf6" />
                            </div>
                            <div>
                                <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Current Subscription: <span style={{ color: '#16539C' }}>{subscription.planId.name}</span></div>
                                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginTop: 2 }}>
                                    Valid until: {new Date(subscription.nextBillingDate || subscription.startDate).toLocaleDateString()}
                                </div>
                            </div>
                        </div>
                        <div style={{ background: '#ecfdf5', color: '#059669', padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700, border: '1px solid #a7f3d0' }}>Active Plan</div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                        {[
                            { label: 'Drivers Limit', current: stats.drivers, limit: subscription.planId.driversLimit !== undefined ? subscription.planId.driversLimit : subscription.planId.operatorsLimit || 'Unlimited', color: '#0ea5e9' },
                            { label: 'Fleet Limit', current: stats.fleet, limit: subscription.planId.fleetLimit || 'Unlimited', color: '#ef4444' },
                            { label: 'Users Limit', current: stats.members, limit: subscription.planId.usersLimit || 'Unlimited', color: '#f59e0b' },
                            { label: 'Routes Limit', current: stats.routes, limit: subscription.planId.routesLimit || 'Unlimited', color: '#10b981' },
                            { label: 'Passenger Limit', current: stats.passengers, limit: subscription.planId.passengerLimit || 'Unlimited', color: '#8b5cf6' },
                            { label: 'Clients Limit', current: stats.clients, limit: subscription.planId.clientsLimit || 'Unlimited', color: '#3b82f6' }
                        ].map(item => {
                            const isUnlimited = item.limit === 'Unlimited' || item.limit === -1 || item.limit === '-1' || item.limit === null;
                            const limitVal = isUnlimited ? Infinity : Number(item.limit);
                            const displayLimit = isUnlimited ? '∞' : limitVal;
                            const pct = isUnlimited ? 0 : Math.min(100, (item.current / limitVal) * 100);
                            
                            return (
                                <div key={item.label} style={{ background: '#f8fafc', borderRadius: 12, padding: '16px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{item.label}</span>
                                        <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{item.current} / <span style={{ color: '#94a3b8' }}>{displayLimit}</span></span>
                                    </div>
                                    {!isUnlimited && (
                                        <div style={{ height: 6, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${pct}%`, background: pct > 90 ? '#ef4444' : pct > 75 ? '#f59e0b' : item.color, borderRadius: 99, transition: 'width 1s ease' }} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Submit Form */}
                <div style={{ background: 'white', borderRadius: '20px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                    <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>Submit a New Request</h2>
                    <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>Need to increase your limits or upgrade your plan? Describe your requirements below.</p>
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="E.g., I need to increase my active drivers limit by 10..."
                            style={{
                                flex: 1, minHeight: '160px', padding: '16px', borderRadius: '12px', border: '1px solid #cbd5e1',
                                fontSize: '15px', color: '#0f172a', resize: 'vertical', outline: 'none', transition: 'border-color 0.2s', fontFamily: FONT
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#16539C'}
                            onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            required
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button type="submit" disabled={submitting || !message.trim()} style={{
                                background: 'linear-gradient(135deg, #16539C, #1e40af)', color: 'white', padding: '12px 24px', borderRadius: '10px',
                                fontWeight: '600', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                                opacity: (submitting || !message.trim()) ? 0.6 : 1, transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(22, 83, 156, 0.2)'
                            }}>
                                {submitting ? 'Submitting...' : 'Send Request'}
                                <Icon d={icons.send} size={18} />
                            </button>
                        </div>
                    </form>
                </div>

                {/* Previous Requests */}
                <div style={{ background: 'white', borderRadius: '20px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', maxHeight: '500px' }}>
                    <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', marginBottom: '20px' }}>Your Previous Requests</h2>
                    <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><p style={{ color: '#64748b' }}>Loading requests...</p></div>
                        ) : requests.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                                    <Icon d={icons.clock} size={40} color="#cbd5e1" />
                                </div>
                                <p style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>You haven't made any requests yet.</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {requests.map(req => {
                                    const style = getStatusStyle(req.status);
                                    return (
                                        <div key={req._id} style={{ padding: '16px', border: `1px solid ${style.border}`, borderRadius: '12px', background: style.bg, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: style.text, fontSize: '13px', fontWeight: '700' }}>
                                                    <Icon d={style.icon} size={14} />
                                                    {req.status}
                                                </div>
                                                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
                                                    {new Date(req.createdAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <p style={{ margin: 0, fontSize: '14px', color: '#334155', lineHeight: '1.5' }}>{req.message}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
